import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import {
  VideoTrack,
  type TrackReference,
  type TrackReferenceOrPlaceholder,
} from "@livekit/components-react";
import { Maximize, Minimize, ZoomIn, ZoomOut } from "lucide-react";

const MIN_ZOOM = 1; // fit (1×) — can never zoom out smaller than this
const MAX_ZOOM = 3; // sensible ceiling per AC2
const ZOOM_STEP = 0.25; // 25% per click

interface ScreenShareViewerProps {
  /** The screen-share track to render. */
  trackRef: TrackReferenceOrPlaceholder;
  /** Extra classes for the outer container (e.g. h-full w-full). */
  className?: string;
}

/**
 * Shared zoom / pan / fullscreen wrapper for a screen-share tile. Used by BOTH
 * the authenticated in-call view (GridLayout/SpeakerLayout/SidebarLayout → CallTile) and the guest
 * meeting-room view (MeetingRoom → ParticipantTile) so the two stay at parity
 * (GH-1067 AC8).
 *
 * - Zoom: digital zoom via CSS `scale`, 1×–3× in 25% steps. Clamped at fit (1×)
 *   so we never introduce extra empty space below the default (AC2/AC3).
 * - Pan: drag to move the magnified region; disabled at fit where there's
 *   nothing to move (AC4). Translation is clamped so the content edges can't be
 *   dragged past the viewport.
 * - Fullscreen: the Fullscreen API on this container only (not the whole stage);
 *   Esc / re-click restores (AC5). Controls are overlaid on the tile.
 * - The video keeps `object-contain` so nothing is cropped by default (AC6);
 *   zoom is the only thing that enlarges it.
 *
 * Because this component is rendered ONLY for an active screen-share, its mere
 * presence satisfies "controls visible only while sharing" (AC1).
 */
export function ScreenShareViewer({ trackRef, className }: ScreenShareViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Reset to fit whenever the shared track changes (share ends / presenter
  // switches) so stale zoom/pan never carries onto different content.
  const trackKey = `${trackRef.participant.sid}-${trackRef.source}`;
  useLayoutEffect(() => {
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
  }, [trackKey]);

  // Clamp pan so the scaled content can't be dragged off-screen. At scale s the
  // element overflows the container by (s-1)/2 on each side.
  const clampPan = useCallback((next: { x: number; y: number }, scale: number) => {
    const el = containerRef.current;
    if (!el || scale <= 1) return { x: 0, y: 0 };
    const maxX = ((scale - 1) * el.clientWidth) / 2;
    const maxY = ((scale - 1) * el.clientHeight) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, next.x)),
      y: Math.max(-maxY, Math.min(maxY, next.y)),
    };
  }, []);

  const zoomTo = useCallback(
    (next: number) => {
      const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(next * 100) / 100));
      setZoom(clamped);
      // Re-clamp pan against the new scale (snaps back to 0 at fit).
      setPan((p) => clampPan(p, clamped));
    },
    [clampPan],
  );

  const zoomIn = useCallback(() => zoomTo(zoom + ZOOM_STEP), [zoom, zoomTo]);
  const zoomOut = useCallback(() => zoomTo(zoom - ZOOM_STEP), [zoom, zoomTo]);

  // ── Pan (pointer drag) — only meaningful past fit ──
  const dragRef = useRef<{ id: number; startX: number; startY: number; origin: { x: number; y: number } } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (zoom <= 1) return;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = { id: e.pointerId, startX: e.clientX, startY: e.clientY, origin: pan };
    },
    [zoom, pan],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      const d = dragRef.current;
      if (!d || d.id !== e.pointerId) return;
      const next = { x: d.origin.x + (e.clientX - d.startX), y: d.origin.y + (e.clientY - d.startY) };
      setPan(clampPan(next, zoom));
    },
    [zoom, clampPan],
  );

  const endDrag = useCallback((e: ReactPointerEvent) => {
    if (dragRef.current?.id === e.pointerId) dragRef.current = null;
  }, []);

  // ── Fullscreen (this container only) ──
  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen();
    }
  }, []);

  // Sync fullscreen state with the browser (covers Esc + native exit).
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const panActive = zoom > 1;

  return (
    <div
      ref={containerRef}
      data-testid="screen-share-viewer"
      data-zoom={zoom}
      data-fullscreen={isFullscreen || undefined}
      className={`relative overflow-hidden bg-muted ${className ?? ""}`}
    >
      <div
        className="h-full w-full"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{ cursor: panActive ? (dragRef.current ? "grabbing" : "grab") : "default", touchAction: "none" }}
      >
        <VideoTrack
          // Only rendered for a live screen-share, so the placeholder case of
          // the union never occurs at runtime.
          trackRef={trackRef as TrackReference}
          className="h-full w-full object-contain"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "center center",
            // No CSS transition while dragging so panning tracks the cursor 1:1;
            // smooth only for button-driven zoom steps.
            transition: dragRef.current ? "none" : "transform 120ms ease-out",
          }}
        />
      </div>

      {/* Control cluster — overlaid on the tile (AC1: present only while a
          screen-share renders this component). */}
      <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full px-1.5 py-1 backdrop-blur-sm"
        style={{ background: "rgba(0,0,0,0.6)" }}
        // Keep clicks on the controls from starting a pan drag.
        onPointerDown={(e) => e.stopPropagation()}
      >
        <ControlButton
          label="Zoom out"
          testid="screen-share-zoom-out"
          disabled={zoom <= MIN_ZOOM}
          onClick={zoomOut}
        >
          <ZoomOut className="size-4" />
        </ControlButton>
        <span
          className="min-w-[3ch] text-center text-xs font-medium tabular-nums text-white/90 select-none"
          data-testid="screen-share-zoom-level"
        >
          {Math.round(zoom * 100)}%
        </span>
        <ControlButton
          label="Zoom in"
          testid="screen-share-zoom-in"
          disabled={zoom >= MAX_ZOOM}
          onClick={zoomIn}
        >
          <ZoomIn className="size-4" />
        </ControlButton>
        <span className="mx-0.5 h-5 w-px bg-white/25" aria-hidden />
        <ControlButton
          label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          testid="screen-share-fullscreen"
          onClick={toggleFullscreen}
        >
          {isFullscreen ? <Minimize className="size-4" /> : <Maximize className="size-4" />}
        </ControlButton>
      </div>
    </div>
  );
}

interface ControlButtonProps {
  label: string;
  testid: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}

function ControlButton({ label, testid, disabled, onClick, children }: ControlButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-testid={testid}
      disabled={disabled}
      onClick={onClick}
      className="flex size-8 items-center justify-center rounded-full text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-35"
    >
      {children}
    </button>
  );
}
